import type { ReactNode } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { Opportunity, Stage, ContactSummary, StageHistoryEntry } from '../../types';
import { fetchOpportunity, updateOpportunity, deleteOpportunity, transitionOpportunityStage, fetchStages, fetchContact } from '../../api';
import { formatCurrency } from '../../hooks/usePipelineData';
import { ActivityTimeline } from '../../components/ActivityTimeline';

// Note type for deal notes
interface DealNote {
  id: string;
  content: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

// ----- Sub-Components -----

interface StageBadgeProps {
  stage?: Stage;
}

function StageBadge({ stage }: StageBadgeProps): ReactNode {
  if (!stage) return <span className="text-gray-400">—</span>;

  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: `${stage.color}20`,
        color: stage.color,
        border: `1px solid ${stage.color}40`,
      }}
    >
      {stage.name}
    </span>
  );
}

interface InlineEditProps {
  value: string;
  onSave: (value: string) => void;
  type?: 'text' | 'number' | 'date';
  placeholder?: string;
  className?: string;
  formatDisplay?: (value: string) => string;
}

function InlineEdit({
  value,
  onSave,
  type = 'text',
  placeholder = '—',
  className = '',
  formatDisplay,
}: InlineEditProps): ReactNode {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        className={`w-full ${className}`}
      />
    );
  }

  const displayValue = formatDisplay ? formatDisplay(value) : value;

  return (
    <span
      onClick={() => {
        setEditValue(value);
        setEditing(true);
      }}
      className={`cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1 ${className}`}
      title="Click to edit"
    >
      {displayValue || <span className="text-gray-400">{placeholder}</span>}
    </span>
  );
}

interface StageDropdownProps {
  currentStageId: string;
  stages: Stage[];
  onStageChange: (stageId: string) => void;
  disabled?: boolean;
}

function StageDropdown({ currentStageId, stages, onStageChange, disabled }: StageDropdownProps): ReactNode {
  return (
    <select
      value={currentStageId}
      onChange={(e) => onStageChange(e.target.value)}
      disabled={disabled}
      className="w-full"
    >
      {stages.map((stage) => (
        <option key={stage.id} value={stage.id}>
          {stage.name} ({stage.probability}%)
        </option>
      ))}
    </select>
  );
}

interface StageHistoryTimelineProps {
  history: StageHistoryEntry[];
  stages: Stage[];
}

function StageHistoryTimeline({ history, stages }: StageHistoryTimelineProps): ReactNode {
  const stageMap = new Map(stages.map((s) => [s.id, s]));

  if (!history || history.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-8">
        No stage history recorded
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
      <div className="space-y-4">
        {history.map((entry, index) => {
          const stage = stageMap.get(entry.stage_id);
          const enteredDate = new Date(entry.entered_at);
          const exitedDate = entry.exited_at ? new Date(entry.exited_at) : null;
          const duration = exitedDate
            ? Math.ceil((exitedDate.getTime() - enteredDate.getTime()) / (1000 * 60 * 60 * 24))
            : null;

          return (
            <div key={index} className="relative pl-10">
              <div
                className="absolute left-2 w-5 h-5 rounded-full border-2 border-white"
                style={{ backgroundColor: stage?.color || '#9ca3af' }}
              />
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{stage?.name || 'Unknown Stage'}</span>
                  {duration !== null && (
                    <span className="text-xs text-gray-500">{duration} day{duration !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {enteredDate.toLocaleDateString()} {enteredDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {exitedDate && ` → ${exitedDate.toLocaleDateString()}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface NotesSectionProps {
  notes: DealNote[];
  onAddNote: (content: string) => void;
  onEditNote: (id: string, content: string) => void;
  onTogglePin: (id: string) => void;
  onDeleteNote: (id: string) => void;
}

function NotesSection({ notes, onAddNote, onEditNote, onTogglePin, onDeleteNote }: NotesSectionProps): ReactNode {
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const handleAddNote = () => {
    if (newNote.trim()) {
      onAddNote(newNote.trim());
      setNewNote('');
    }
  };

  const handleSaveEdit = (id: string) => {
    if (editContent.trim()) {
      onEditNote(id, editContent.trim());
    }
    setEditingId(null);
    setEditContent('');
  };

  const pinnedNotes = notes.filter((n) => n.pinned);
  const unpinnedNotes = notes.filter((n) => !n.pinned);
  const sortedNotes = [...pinnedNotes, ...unpinnedNotes];

  return (
    <div className="card">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Notes</h2>

      {/* Add new note */}
      <div className="mb-4">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          className="w-full h-20 resize-none text-sm"
        />
        <button
          className="btn btn-secondary w-full mt-2 text-sm"
          onClick={handleAddNote}
          disabled={!newNote.trim()}
        >
          Add Note
        </button>
      </div>

      {/* Notes list */}
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {sortedNotes.map((note) => (
          <div
            key={note.id}
            className={`relative p-3 rounded-lg ${note.pinned ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}
          >
            {note.pinned && (
              <span className="absolute top-1 right-1 text-yellow-500 text-xs">📌</span>
            )}
            {editingId === note.id ? (
              <div>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full h-20 resize-none text-sm mb-2"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    className="btn btn-secondary text-xs flex-1"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary text-xs flex-1"
                    onClick={() => handleSaveEdit(note.id)}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-900 whitespace-pre-wrap pr-6">{note.content}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400">
                    {new Date(note.updated_at).toLocaleDateString()}
                  </span>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => onTogglePin(note.id)}
                    >
                      {note.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => {
                        setEditingId(note.id);
                        setEditContent(note.content);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="text-xs text-ds-danger hover:underline"
                      onClick={() => onDeleteNote(note.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        {notes.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-4">No notes yet</div>
        )}
      </div>
    </div>
  );
}

interface CloseDealModalProps {
  isOpen: boolean;
  type: 'won' | 'lost';
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

function CloseDealModal({ isOpen, type, onClose, onConfirm }: CloseDealModalProps): ReactNode {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Mark Deal as {type === 'won' ? 'Won 🎉' : 'Lost'}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {type === 'won'
              ? 'Congratulations! Add any notes about closing this deal.'
              : 'What was the reason for losing this deal?'}
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={type === 'won' ? 'Closing notes (optional)...' : 'Reason for loss...'}
            className="w-full h-24 resize-none mb-4"
          />
          <div className="flex gap-3">
            <button className="btn btn-secondary flex-1" onClick={onClose}>
              Cancel
            </button>
            <button
              className={`btn flex-1 ${type === 'won' ? 'bg-ds-success text-white hover:bg-green-600' : 'btn-danger'}`}
              onClick={handleConfirm}
            >
              {type === 'won' ? 'Mark Won' : 'Mark Lost'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  isOpen: boolean;
  dealName: string;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteConfirmModal({ isOpen, dealName, onClose, onConfirm }: DeleteConfirmModalProps): ReactNode {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Deal</h3>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to delete <strong>{dealName}</strong>? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button className="btn btn-secondary flex-1" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-danger flex-1" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Main Component -----

export function OpportunityDetail(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data state
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [contact, setContact] = useState<ContactSummary | null>(null);
  const [notes, setNotes] = useState<DealNote[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [closeModalType, setCloseModalType] = useState<'won' | 'lost' | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Load data
  useEffect(() => {
    async function loadData() {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [oppData, stagesData] = await Promise.all([
          fetchOpportunity(id),
          fetchStages(),
        ]);

        setOpportunity(oppData);
        setStages(stagesData);

        // Load contact if linked
        if (oppData.contact_id) {
          try {
            const contactData = await fetchContact(oppData.contact_id);
            setContact(contactData);
          } catch {
            // Contact fetch is optional
          }
        }

        // Parse notes from opportunity metadata or notes field
        if (oppData.notes) {
          try {
            const parsedNotes = JSON.parse(oppData.notes);
            if (Array.isArray(parsedNotes)) {
              setNotes(parsedNotes);
            }
          } catch {
            // If notes is plain text, create a single note
            setNotes([{
              id: 'legacy-1',
              content: oppData.notes,
              pinned: false,
              created_at: oppData.created_at,
              updated_at: oppData.updated_at,
            }]);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load deal');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  // Get current stage
  const currentStage = stages.find((s) => s.id === opportunity?.stage_id);

  // Handlers
  const handleUpdateField = useCallback(async (field: keyof Opportunity, value: string | number) => {
    if (!opportunity || !id) return;

    try {
      setSaving(true);
      const updated = await updateOpportunity(id, { [field]: value });
      setOpportunity(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  }, [opportunity, id]);

  const handleStageChange = useCallback(async (newStageId: string) => {
    if (!opportunity || !id || newStageId === opportunity.stage_id) return;

    try {
      setSaving(true);
      const newStage = stages.find((s) => s.id === newStageId);
      const updated = await transitionOpportunityStage(id, newStageId, newStage?.probability);
      setOpportunity(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change stage');
    } finally {
      setSaving(false);
    }
  }, [opportunity, id, stages]);

  const handleCloseDeal = useCallback(async (type: 'won' | 'lost', reason: string) => {
    if (!opportunity || !id) return;

    try {
      setSaving(true);
      const terminalStage = stages.find((s) => s.is_terminal && s.terminal_type === type);
      
      const updates: Partial<Opportunity> = {
        closed_reason: type,
        closed_reason_note: reason,
        actual_close_date: new Date().toISOString(),
      };

      if (terminalStage) {
        updates.stage_id = terminalStage.id;
        updates.probability = terminalStage.probability;
      }

      const updated = await updateOpportunity(id, updates);
      setOpportunity(updated);
      setCloseModalType(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close deal');
    } finally {
      setSaving(false);
    }
  }, [opportunity, id, stages]);

  const handleDelete = useCallback(async () => {
    if (!id) return;

    try {
      setSaving(true);
      await deleteOpportunity(id);
      navigate('/pipeline');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete deal');
      setSaving(false);
    }
  }, [id, navigate]);

  // Notes handlers
  const handleAddNote = useCallback(async (content: string) => {
    if (!opportunity || !id) return;

    const newNote: DealNote = {
      id: `note-${Date.now()}`,
      content,
      pinned: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const updatedNotes = [...notes, newNote];
    setNotes(updatedNotes);

    try {
      await updateOpportunity(id, { notes: JSON.stringify(updatedNotes) });
    } catch (err) {
      setNotes(notes); // Revert on error
      setError(err instanceof Error ? err.message : 'Failed to add note');
    }
  }, [opportunity, id, notes]);

  const handleEditNote = useCallback(async (noteId: string, content: string) => {
    if (!id) return;

    const updatedNotes = notes.map((n) =>
      n.id === noteId ? { ...n, content, updated_at: new Date().toISOString() } : n
    );
    setNotes(updatedNotes);

    try {
      await updateOpportunity(id, { notes: JSON.stringify(updatedNotes) });
    } catch (err) {
      setNotes(notes);
      setError(err instanceof Error ? err.message : 'Failed to edit note');
    }
  }, [id, notes]);

  const handleTogglePin = useCallback(async (noteId: string) => {
    if (!id) return;

    const updatedNotes = notes.map((n) =>
      n.id === noteId ? { ...n, pinned: !n.pinned } : n
    );
    setNotes(updatedNotes);

    try {
      await updateOpportunity(id, { notes: JSON.stringify(updatedNotes) });
    } catch (err) {
      setNotes(notes);
    }
  }, [id, notes]);

  const handleDeleteNote = useCallback(async (noteId: string) => {
    if (!id) return;

    const updatedNotes = notes.filter((n) => n.id !== noteId);
    setNotes(updatedNotes);

    try {
      await updateOpportunity(id, { notes: JSON.stringify(updatedNotes) });
    } catch (err) {
      setNotes(notes);
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    }
  }, [id, notes]);

  // Loading state
  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-500">Loading deal...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !opportunity) {
    return (
      <div className="page-container">
        <div className="card text-center py-12">
          <p className="text-ds-danger mb-4">{error}</p>
          <Link to="/pipeline" className="btn btn-secondary">
            Back to Pipeline
          </Link>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="page-container">
        <div className="card text-center py-12">
          <p className="text-gray-500 mb-4">Deal not found</p>
          <Link to="/pipeline" className="btn btn-secondary">
            Back to Pipeline
          </Link>
        </div>
      </div>
    );
  }

  const isClosed = opportunity.closed_reason === 'won' || opportunity.closed_reason === 'lost';

  return (
    <div className="page-container">
      {/* Back link */}
      <div className="mb-4">
        <Link to="/pipeline" className="text-ds-primary hover:underline text-sm">
          ← Back to Pipeline
        </Link>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-ds-danger rounded-lg p-3 mb-4 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-ds-danger hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Saving indicator */}
      {saving && (
        <div className="fixed top-4 right-4 bg-ds-primary text-white px-4 py-2 rounded-lg shadow-lg z-50">
          Saving...
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left 2/3 */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header Card */}
          <div className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-xl font-semibold text-gray-900">
                    <InlineEdit
                      value={opportunity.name}
                      onSave={(v) => handleUpdateField('name', v)}
                      className="text-xl font-semibold"
                    />
                  </h1>
                  <StageBadge stage={currentStage} />
                  {isClosed && (
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        opportunity.closed_reason === 'won'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {opportunity.closed_reason === 'won' ? '🏆 Won' : '❌ Lost'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Created {new Date(opportunity.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Value</label>
                <p className="text-2xl font-semibold text-gray-900">
                  <InlineEdit
                    value={String(opportunity.amount)}
                    onSave={(v) => handleUpdateField('amount', parseFloat(v) || 0)}
                    type="number"
                    formatDisplay={(v) => formatCurrency(parseFloat(v) || 0, opportunity.currency)}
                    className="text-2xl font-semibold"
                  />
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Probability</label>
                <p className="text-gray-900">
                  <InlineEdit
                    value={String(opportunity.probability)}
                    onSave={(v) => handleUpdateField('probability', parseInt(v) || 0)}
                    type="number"
                    formatDisplay={(v) => `${v}%`}
                  />
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Expected Close</label>
                <p className="text-gray-900">
                  <InlineEdit
                    value={opportunity.expected_close_date?.split('T')[0] || ''}
                    onSave={(v) => handleUpdateField('expected_close_date', v)}
                    type="date"
                    formatDisplay={(v) => v ? new Date(v).toLocaleDateString() : ''}
                    placeholder="Not set"
                  />
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Assigned To</label>
                <p className="text-gray-900">
                  <InlineEdit
                    value={opportunity.assigned_to || ''}
                    onSave={(v) => handleUpdateField('assigned_to', v)}
                    placeholder="Unassigned"
                  />
                </p>
              </div>
            </div>
          </div>

          {/* Contact Info Card */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Contact</h2>
            {contact ? (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-ds-primary text-white flex items-center justify-center font-medium text-lg">
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{contact.name}</h3>
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="text-sm text-ds-primary hover:underline">
                      {contact.email}
                    </a>
                  )}
                  {contact.company && (
                    <p className="text-sm text-gray-500">{contact.company}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                No contact linked to this deal
              </div>
            )}
          </div>

          {/* Stage History Card */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Stage History</h2>
            <StageHistoryTimeline
              history={opportunity.stage_history || []}
              stages={stages}
            />
          </div>

          {/* Activities - Using the ActivityTimeline component */}
          {id && (
            <ActivityTimeline
              parentType="opportunity"
              parentId={id}
              showAddButton
            />
          )}
        </div>

        {/* Sidebar - Right 1/3 */}
        <div className="space-y-4">
          {/* Actions Card */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Actions</h2>
            <div className="flex flex-col gap-2">
              {!isClosed && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-2">Move Stage</label>
                    <StageDropdown
                      currentStageId={opportunity.stage_id}
                      stages={stages.filter((s) => !s.is_terminal)}
                      onStageChange={handleStageChange}
                      disabled={saving}
                    />
                  </div>
                  <hr className="my-2" />
                  <button
                    className="btn w-full bg-ds-success text-white hover:bg-green-600"
                    onClick={() => setCloseModalType('won')}
                    disabled={saving}
                  >
                    🎉 Mark Won
                  </button>
                  <button
                    className="btn btn-secondary w-full text-ds-danger"
                    onClick={() => setCloseModalType('lost')}
                    disabled={saving}
                  >
                    Mark Lost
                  </button>
                </>
              )}
              <hr className="my-2" />
              <button
                className="btn btn-danger w-full"
                onClick={() => setShowDeleteModal(true)}
                disabled={saving}
              >
                Delete Deal
              </button>
            </div>
          </div>

          {/* Closed Reason (if closed) */}
          {isClosed && opportunity.closed_reason_note && (
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                {opportunity.closed_reason === 'won' ? 'Closing Notes' : 'Loss Reason'}
              </h2>
              <p className="text-sm text-gray-600">{opportunity.closed_reason_note}</p>
              {opportunity.actual_close_date && (
                <p className="text-xs text-gray-400 mt-2">
                  Closed on {new Date(opportunity.actual_close_date).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {/* Notes Card */}
          <NotesSection
            notes={notes}
            onAddNote={handleAddNote}
            onEditNote={handleEditNote}
            onTogglePin={handleTogglePin}
            onDeleteNote={handleDeleteNote}
          />
        </div>
      </div>

      {/* Modals */}
      <CloseDealModal
        isOpen={closeModalType !== null}
        type={closeModalType || 'won'}
        onClose={() => setCloseModalType(null)}
        onConfirm={(reason) => closeModalType && handleCloseDeal(closeModalType, reason)}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        dealName={opportunity.name}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

export default OpportunityDetail;
